import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Colors from 'enums/Colors';
import ComponentInteractionModel from 'models/componentInteraction/ComponentInteraction';
import { ComponentMarkText } from 'models/componentInteraction/ComponentMarkText';
import { TextDangerouslySetInnerHtml } from '../../RecursosConteudoPage.styled';
import { FaCheckCircle } from 'react-icons/fa';

const Styled = {
    Container: styled.div`
        display: flex;
        align-items: center;
        flex-direction: column;
        gap: 8px;
    `,

    Title: styled.h1`
        font-family: 'Barlow';
        font-weight: 600;
        font-size: 18px;
        line-height: 25.2px;
        letter-spacing: 0%;
        color: ${Colors.Blue};
    `,

    RowBetween: styled.div`
        display: flex;
        align-items: center;
        justify-content: space-between;
    `,

    SubTitle: styled.h2`
        font-family: 'Barlow';
        font-weight: 600;
        font-size: 14px;
        line-height: 19.6px;
        letter-spacing: 0%;
        color: ${Colors.Blue};
    `,

    Column: styled.div`
        display: flex;
        flex-direction: column;
        gap: 8px;
    `,

    TextMark: {
        Container: styled.div<{ isVisible: boolean; delay: number }>`
            display: flex;
            gap: 16px;
            min-height: 80px;

            width: 100%;
            max-width: 668px;
            box-sizing: border-box;

            opacity: 0;
            transform: translateX(50px);
            transition: opacity 0.6s ease-out, transform 0.6s ease-out;
            transition-delay: ${({ delay }) => delay}ms;

            ${({ isVisible }) =>
                isVisible &&
                `
                opacity: 1;
                transform: translateX(0);
            `}

            @media (max-width: 720px) {
                max-width: 100%;
                flex-direction: row;
            }
        `,

        MarkNumber: styled.div<{ color: string }>`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 35px !important;
            height: 35px !important;
            aspect-ratio: 1/1;
            border-radius: 50%;
            background-color: ${({ color }) => (color ? color : Colors.Grey10)};
            font-family: 'Barlow';
            font-weight: 600;
            font-size: 18px;
            color: ${({ color }) => (color !== Colors.Grey10 ? Colors.White : '')};
        `,

        Bullet: styled.div<{ color: string }>`
            background-color: ${({ color }) => (color ? color : Colors.Grey10)};
            width: 35px !important;
            height: 35px !important;
            aspect-ratio: 1/1;
            border-radius: 50%;
            padding: 10px;
            gap: 16px;
        `,

        CheckContainer: styled.div<{ color: string }>`
            background-color: ${({ color }) => (color ? color : Colors.Grey10)};
            display: flex;
            align-items: center;
            justify-content: center;
            width: 35px !important;
            height: 35px !important;
            aspect-ratio: 1/1;
            border-radius: 50%;
            flex-shrink: 0;
        `,

        Paragraf: styled.p`
            font-family: 'Barlow';
            font-weight: 400;
            font-size: 18px;
            line-height: 25.2px;
            letter-spacing: 0%;
        `,
    },
};

interface ITextMark {
    typeMark: number;
    textMark: ComponentMarkText;
    color: string;
    delay: number;
    isVisible: boolean;
    index: number;
}

const TextMark = ({ typeMark, textMark, color, delay, isVisible, index }: ITextMark) => {
    return (
        <Styled.TextMark.Container isVisible={isVisible} delay={delay}>
            {typeMark === 0 ? (
                <Styled.TextMark.MarkNumber color={color}>{index + 1}</Styled.TextMark.MarkNumber>
            ) : typeMark === 1 ? (
                <Styled.TextMark.Bullet color={color} />
            ) : (
                <Styled.TextMark.CheckContainer color={color}>
                    <FaCheckCircle size={18} color={Colors.White} />
                </Styled.TextMark.CheckContainer>
            )}
            <TextDangerouslySetInnerHtml
                style={{ margin: 0, maxWidth: '100%', wordBreak: 'break-word', overflowWrap: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: textMark.text }}
            />
        </Styled.TextMark.Container>
    );
};

interface IComponentMarkPublic {
    dados: ComponentInteractionModel;
}

const ComponentMarkPublic = ({ dados }: IComponentMarkPublic) => {
    const [visibleIndexes, setVisibleIndexes] = useState<number[]>([]);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const marksList: ComponentMarkText[] = [...(dados.marks?.marks ?? [])].sort((a, b) => a.ordem - b.ordem);

    useEffect(() => {
        if (marksList.length === 0) return;

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        // Ativar um por um com delay diferente para cada um
                        marksList.forEach((_, index) => {
                            setTimeout(() => {
                                setVisibleIndexes(prev => [...prev, index]);
                            }, index * 300); // Delay progressivo de 300ms para cada item
                        });

                        observer.disconnect(); // Evita chamar novamente após a primeira ativação
                    }
                });
            },
            { threshold: 0.2 } // Ativa quando 20% do container entra na tela
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect(); // Cleanup
    }, [marksList]);

    return (
        <Styled.Container ref={containerRef}>
            {marksList.map((text, i) => (
                <TextMark
                    key={text.codigo}
                    index={i}
                    color={dados.cor ?? Colors.Grey10}
                    typeMark={dados.marks?.typeMark ?? 0}
                    textMark={text}
                    delay={i * 300} // Delay progressivo
                    isVisible={visibleIndexes.includes(i)} // Ativa apenas os visíveis
                />
            ))}
        </Styled.Container>
    );
};

export default ComponentMarkPublic;
